/**
 * Sims page for managing simulation records, including viewing, editing, deleting,
 * and restoring simulations with pagination, filtering, and permission checks.
 *
 * @module pages/sim/Sims
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import React, {useState, useEffect} from "react";
import PropTypes from "prop-types";
import moment from "moment";
import {connect} from "react-redux";
import Avatar from "@material-ui/core/Avatar";
import Paper from "@material-ui/core/Paper";
import {withStyles, createTheme} from "@material-ui/core/styles";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import {CircularProgress, ThemeProvider} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Tooltip from "@material-ui/core/Tooltip";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import VisibilityIcon from "@material-ui/icons/Visibility";
import ScreenShareIcon from "@material-ui/icons/ScreenShare";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import {indigo} from "@material-ui/core/colors";
import {Alert} from "@material-ui/lab";
import {useHistory} from "react-router-dom";
import {
    fetchData,
    initializeNewSim,
    setEditSimStep,
    fetchProductList,
    deleteAssignmentDetails,
    saveSelectedSim,
    reset,
    resetVideoStats,
    fetchDeletedSims,
    saveCurrentSimProductType
} from "../../redux/sims/actions";
import StyledLink from "../../components/common/StyledLink";
import DetailsScreen from "../../components/sim/DetailsScreen.jsx";
import {
    getSimById,
    deleteSim,
    saveAvatarDetails,
} from "../../services/result.service";
import {sfReset} from '../../redux/sf/actions';
import {useLocation} from "react-router-dom";
import noSims from "../../assets/images/noSims.png";
import SimsPermissions from "../../permissions/sims";
import {permissionCheck} from "../../utils/permissionCheck";
import {useTableStyles} from "./Sims.js";
import {Redirect} from 'react-router-dom';
import RestoreFromTrashIcon from '@material-ui/icons/RestoreFromTrash';
import ProfilePic from '../../assets/images/profile_pic.png'
import Pagination from '@material-ui/lab/Pagination';
import {recoverSim, deleteAll} from '../../services/result.service'
import EnhancedTableToolbar from "../../components/sim/EnhancedTableToolbar.jsx";

/**
 * A hook that parses the query string from the current URL and returns an instance of `URLSearchParams`.
 *
 * This utility is commonly used to extract query parameters from the URL in a React application
 * where `useLocation` is provided, such as when using React Router.
 *
 * @function useQuery
 * @returns {URLSearchParams} An instance of `URLSearchParams` containing the parsed query parameters.
 */
const useQuery = () => new URLSearchParams(useLocation().search);

/**
 * A customized Material-UI theme object created using the createTheme function.
 * This theme defines the color palette settings tailored to the application.
 *
 * Properties:
 * - palette: Contains color configurations for the theme.
 *   - primary: Specifies the primary color of the theme, set to indigo.
 */
const innerTheme = createTheme({
    palette: {
        primary: indigo,
    },
});

/**
 * A customized table row component styled using Material-UI's `withStyles` higher-order component.
 * This component applies alternate row styling by changing the background color of even rows
 * to match the theme's action hover color.
 *
 * The styling is achieved by targeting the `nth-of-type(even)` CSS selector.
 *
 * @constant {Component} StyledTableRow
 */
const StyledTableRow = withStyles((theme) => ({
    root: {
        "&:nth-of-type(even)": {
            backgroundColor: theme.palette.action.hover,
        },
    },
}))(TableRow);

const limit = 10;

/**
 * The `Sims` component encapsulates the functionality for managing simulation-related data and operations.
 * It involves rendering and managing a list of simulations, handling pagination, filtering, sorting, and
 * executing various simulation-related actions like viewing details, editing, deleting, restoring,
 * and interacting with related user data and configurations.
 *
 * @param {Object} params - The parameters for the `Sims` component.
 * @param {number} params.totalSims - The total number of simulations available.
 * @param {Array<Object>} params.sims - The list of current simulation records.
 * @param {Function} params.fetchData - Function to fetch paginated and filtered simulation data.
 * @param {Function} params.initializeNewSim - Function to initialize a new simulation process.
 * @param {Function} params.setEditSimStep - Function to set up the editing step for an existing simulation.
 * @param {Function} params.fetchProductList - Function to fetch the list of available products.
 * @param {Function} params.deleteAssignmentDetails - Function to delete assignment-related data for a simulation.
 * @param {Function} params.saveSelectedSim - Function to save the currently selected simulation details.
 * @param {Object} params.selectedSim - The currently selected simulation.
 * @param {Array<Object>} params.products - The list of available products associated with simulations.
 * @param {Object} params.prop - Configuration properties used for specific component behaviors.
 * @param {Function} params.reset - Function to reset certain simulation-related states.
 * @param {Function} params.resetVideoStats - Function to reset video statistics for simulations.
 * @param {Array<Object>} params.characters - The list of character data associated with simulations.
 * @param {Array<Object>} params.sim_listing - The list of existing simulations fetched.
 * @param {Object} params.userDetails - Details of the currently logged-in user, including roles and permissions.
 * @param {Function} params.sfReset - Function to reset Salesforce-specific configurations.
 * @param {Array<Object>} params.deletedSims - The list of simulations marked as deleted.
 * @param {Function} params.fetchDeletedSims - Function to fetch a list of deleted simulations.
 * @param {number} params.totalDeletedSims - The total number of deleted simulations.
 * @param {Function} params.saveCurrentSimProductType - Function to save the product type associated with the current simulation being worked on.
 *
 * @returns {JSX.Element} The React component rendering the simulation management interface.
 */
const Sims = ({
    totalSims,
    sims,
    fetchData,
    initializeNewSim,
    setEditSimStep,
    fetchProductList,
    deleteAssignmentDetails,
    saveSelectedSim,
    selectedSim,
    products,
    prop,
    reset,
    resetVideoStats,
    characters,
    sim_listing,
    userDetails,
    sfReset,
    deletedSims,
    fetchDeletedSims,
    totalDeletedSims,
    saveCurrentSimProductType
}) => {
    const query = useQuery();
    const history = useHistory();
    const [pageNumber, setPageNumber] = useState(0);
    // const location = useLocation();
    const classes = useTableStyles();
    const [showDetailsScreen, setShowDetailsScreen] = useState(false);
    const [detailsData, setDetailsData] = useState(null);
    const [filterType, setFilterType] = useState("All");
    const [textSearch, setTextSearch] = useState("");
    const [simsList, setSimsList] = useState([]);
    const [assignmentStatus, setAssignmentStatus] = useState(false);
    const [open, setOpen] = useState(false);
    const [currentSim, setCurrentSim] = useState({});
    const [assignMentProps, setAssignmentProps] = useState(prop);
    const [openAlert, setOpenAlert] = React.useState(false);
    const [alertMesage, setAlertMessage] = React.useState("");
    const [page, setPage] = useState(1);
    const [sortBy, setSortBy] = React.useState(null);
    const [order, setOrder] = React.useState(null);
    const [trash, setTrash] = useState(false);
    const [selectedRestoreSim, setSelectedRestoreSim] = useState();
    const [emptyTrash, setEmptyTrash] = useState(false);
    const [emptyListCheck, setEmptyListCheck] = useState(false);

    localStorage.setItem('sims_id', '');

    useEffect(() => {
        if (sims && sims.length > 0) {
            setSimsList(sims);
            reset();
            resetVideoStats();
        } else if (deletedSims && deletedSims.length > 0) {
            setSimsList(deletedSims);
            reset();
        }
    }, [sims, reset, resetVideoStats, deletedSims]);

    // get paginated sims data
    useEffect(() => {
        if (userDetails.role.toLowerCase() !== 'agent') {
            sfReset();
            if (trash) {
                fetchDeletedSims(limit, pageNumber);
            } else if (filterType === "All") {
                fetchData(limit, pageNumber, null, textSearch, sortBy, order);
            } else {
                fetchData(limit, pageNumber, filterType, textSearch, sortBy, order);
            }
        }
    }, [pageNumber, fetchData, filterType, textSearch, userDetails, sfReset, sortBy,
        order, trash, fetchDeletedSims]);

    useEffect(() => {
        setPageNumber(0);
        setSimsList([]);
        setPage(1);
    }, [filterType, textSearch, trash]);

    useEffect(() => {
        fetchProductList();
    }, [fetchProductList]);

    useEffect(() => {
        if (detailsData) {
            setShowDetailsScreen(true);
        }
    }, [detailsData]);

    useEffect(() => {
        if (!showDetailsScreen) {
            setDetailsData(null);
        }
    }, [showDetailsScreen]);

    useEffect(() => {
        if (query.get("exit")) {
            if (Object.keys(selectedSim).length > 0) {
                setDetailsData(selectedSim);
            }
            if (assignMentProps && assignMentProps.type === undefined) {
                setAssignmentProps({
                    ...assignMentProps,
                    type: query.get("type"),
                });
            }
        }
    }, [setDetailsData, query, selectedSim, assignMentProps]);

    useEffect(
        () => {
        },
        [sim_listing]
    );

    useEffect(() => {
        if (emptyTrash) {
            setOpen(true);
        }
        if (deletedSims && deletedSims.length === 0) {
            setTimeout(() => {
                setEmptyListCheck(true);
            }, 3000);
        }
    }, [emptyTrash, deletedSims]);

    if (prop.sim_id && !detailsData && !assignmentStatus) {
        getSimById(prop.sim_id).then((res) => {
            setDetailsData(res.data);
        });
        setAssignmentStatus(true);
    }

    /**
     * Handles the click event on a row, performing several operations:
     * - Stores the `sim_id` of the clicked row in `localStorage`.
     * - Saves the product type of the clicked row in a separate state or storage.
     * - Deletes existing assignment details.
     * - Resets assignment properties to null.
     * - Updates the details data with the clicked row.
     * - Stores the clicked row as the currently selected simulation object.
     *
     * @async
     * @function handleRowClick
     * @param {Object} row - The data object corresponding to the clicked row.
     * @param {string} row._id - The unique identifier for the simulation associated with the row.
     * @param {string} row.product_name - The name of the product associated with the row.
     * @returns {Promise<void>} Resolves when all data updates are completed.
     */
    const handleRowClick = async (row) => {
        const requestData = {}
        requestData.sim_id = row._id
        localStorage.setItem("sim_id", row._id);
        saveCurrentSimProductType(row.product_name);
        deleteAssignmentDetails();
        setAssignmentProps(null);
        setDetailsData(row);
        saveSelectedSim(row);
    };

    /**
     * Asynchronous function to handle click events and perform operations
     * related to simulation characters and navigation.
     *
     * @param {Object} row - The row object representing the current simulation data.
     * @param {Array} row.sim_characters - An array of characters associated with the simulation.
     * @param {string} row._id - The unique identifier of the current simulation row.
     * @param {string} row.product_name - The name of the product associated with the simulation.
     *
     * This function performs the following:
     * 1. Checks if the `sim_characters` array is empty:
     *    - Iterates over a globally defined `characters` array, assigning the `sim_id` property
     *      of each character to the `_id` of the `row`.
     *    - Constructs a `requestData` object containing the updated `characters` as `sim_characters`.
     *    - Calls `saveAvatarDetails` to persist the updated `requestData`.
     * 2. Calls `saveCurrentSimProductType` to save the product name of the current simulation.
     * 3. Calls `setEditSimStep` to update the current simulation step asynchronously.
     * 4. Navigates to a new route using `history.push`, passing the simulation ID (`_id`)
     *    as a query parameter in the URL.
     *
     * @throws {Error} If any asynchronous operation within the function fails, an error will be thrown.
     */
    const handleClick = async (row) => {
        if (!row.sim_characters.length) {
            for (const singleCharacter of characters) {
                singleCharacter.sim_id = row._id;
            }
            const requestData = {sim_characters: characters};
            await saveAvatarDetails(requestData);
        }
        saveCurrentSimProductType(row.product_name);
        await setEditSimStep();
        history.push(`/sims/createsim?simId=${row._id}`);
    };

    /**
     * A function to handle the action of closing a component or dialog.
     * This function sets the `open` state to `false`, typically to signify that a UI element
     * such as a modal, dropdown, or dialog should be closed.
     */
    const handleClose = () => {
        setOpen(false);
    };

    /**
     * Asynchronous function to handle the confirmation and execution of a delete operation for a simulation.
     *
     * This function performs the following steps:
     * 1. Constructs the request data required for the delete operation, including the simulation ID.
     * 2. Calls the deleteSim function to perform the delete operation.
     * 3. Checks the response from the deleteSim function:
     *    - If unsuccessful, displays an alert message and shows an alert dialog.
     * 4. Resets the simulations list and closes the delete confirmation dialog.
     * 5. Resets the current page number to 0.
     * 6. If the user is not an agent, fetches updated data with applied filters, pagination, and sorting based on the current filter settings and user input.
     *
     * @returns {Promise<void>} A promise that resolves when the delete operation and subsequent actions are completed.
     */
    const deleteConfirm = async () => {
        const requestData = {};
        requestData.sim_id = currentSim._id;
        const deleteSimStatus = await deleteSim(requestData);
        if (!deleteSimStatus.data.success) {
            setAlertMessage(deleteSimStatus.data.message);
            setOpenAlert(true);
        }
        setSimsList([]);
        setOpen(false);
        setPageNumber(0);
        if (userDetails.role.toLowerCase() !== 'agent') {
            const skip = page - 1;
            if (filterType === "All") {
                fetchData(limit, skip, null, textSearch, sortBy, order);
            } else {
                fetchData(limit, skip, filterType, textSearch, sortBy, order);
            }
        }
    };

    /**
     * Handles the confirmation process for restoring a deleted simulation.
     *
     * This asynchronous function performs the following steps:
     * 1. Calls the `recoverSim` function using the ID of the selected simulation scheduled for restoration.
     * 2. On successful recovery, resets the simulations list by clearing the current state.
     * 3. Re-fetches the list of deleted simulations with the specified limit and pagination skip value.
     * 4. Closes the restoration confirmation dialog.
     * 5. Resets the pagination to the first page.
     *
     * @async
     * @function restoreConfirm
     * @returns {Promise<void>} A promise that resolves when the restoration process and subsequent updates are complete.
     */
    const restoreConfirm = async () => {
        const recoveredSimStatus = await recoverSim(selectedRestoreSim._id);
        if (recoveredSimStatus.data.success) {
            setSimsList([]);
            const skip = page - 1;
            await fetchDeletedSims(limit, skip);
            setOpen(false);
            setPageNumber(0);
        }
    };

    /**
     * Handles the delete button click event.
     * Opens a confirmation dialog and sets the current item for deletion.
     *
     * @param {Object} row - The data object representing the row to be deleted.
     */
    const handleDeleteClick = (row) => {
        setOpen(true);
        setCurrentSim(row);
    };

    /**
     * Handles the closing of an alert component.
     *
     * @param {Object} event - The event object that triggered the close action.
     * @param {string} reason - The reason for the close action. If the reason is "clickaway", the alert will not be closed.
     *
     * This function ensures the alert is closed by setting its open state to false and clearing its message,
     * unless the close action was triggered due to a "clickaway" interaction.
     */
    const handleAlertClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setOpenAlert(false);
        setAlertMessage("");
    };

    /**
     * Handles the logic for changing the current page in a paginated view.
     *
     * This function updates the current page state and calculates the offset for
     * fetching data based on the selected page. Depending on the context (e.g., if
     * displaying deleted items or filtering by a type), it triggers the appropriate
     * data fetching logic.
     *
     * @param {Object} event - The event object triggered by the page change action.
     * @param {number} newPage - The number of the new page that was selected.
     */
    const handlePageChange = (event, newPage) => {
        setPage(newPage);
        const skip = (newPage - 1);
        if (trash) {
            fetchDeletedSims(limit, skip);
        } else if (filterType === "All") {
            fetchData(limit, skip, null, textSearch, sortBy, order);
        } else {
            fetchData(limit, skip, filterType, textSearch, sortBy, order);
        }
    };

    /**
     * Handles the logic for sorting data based on the provided property.
     *
     * This function determines the sort order (ascending or descending) for
     * a given property and updates the state accordingly. It also returns
     * the new sort order as a string.
     *
     * @param {string} property - The property by which the data should be sorted.
     * @returns {string} - The new sort order, either 'asc' or 'desc'.
     */
    const handleRequestSort = (property) => {
        const isAsc = sortBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        return isAsc ? 'desc' : 'asc';
    };

    /**
     * Function to handle the sorting of properties.
     *
     * This function performs the following operations:
     * 1. Executes the request sort logic based on the provided property.
     * 2. Resets the page number to the initial value.
     * 3. Updates the sorting property state.
     * 4. Clears the current list of simulations.
     *
     * @param {string} property - The property by which sorting should be handled.
     */
    const sortProperties = (property) => {
        handleRequestSort(property);
        setPageNumber(0);
        setSortBy(property);
        setSimsList([]);
    };

    /**
     * Function to handle the restoration process for a SIM card.
     * This function updates the state to set the selected SIM data
     * and opens a modal or dialog related to restoring the SIM.
     *
     * @param {Object} row - The data object representing the SIM card
     *                       that is being restored. This object is
     *                       typically passed from a user action, such
     *                       as selecting a specific row in a table.
     */
    const restoreSim = (row) => {
        setSelectedRestoreSim(row);
        setOpen(true);
    };

    /**
     * Asynchronous function to confirm and handle the deletion of all items.
     *
     * Upon successful completion of the delete operation:
     * - Closes the confirm dialog.
     * - Resets the trash status.
     * - Clears the simulation list.
     * - Updates the empty list check after a delay.
     *
     * Uses the `deleteAll` function to perform the deletion operation and checks the response status.
     */
    const deleteAllConfirm = async () => {
        const result = await deleteAll();
        if (result.data.success) {
            setOpen(false);
            setEmptyTrash(false);
            setSimsList([]);
            setTimeout(() => {
                setEmptyListCheck(true);
            }, 3000);
        }
    };

    if (userDetails.role.toLowerCase() === 'agent' && !prop.sim_id) {
        return (
            <Redirect to={'/dashboard'}/>
        );
    }

    return (
        <>
            <Paper className={userDetails.role.toLowerCase() === 'agent' ? classes.hidePage : classes.paper}>
                <EnhancedTableToolbar
                    initializeNewSim={initializeNewSim}
                    filterType={filterType}
                    setFilterType={setFilterType}
                    setTextSearch={setTextSearch}
                    products={products || []}
                    trash={trash}
                    setTrash={setTrash}
                    setEmptyTrash={setEmptyTrash}
                    simsList={simsList}
                />
                {showDetailsScreen && (
                    <DetailsScreen
                        sim={detailsData}
                        open={showDetailsScreen}
                        handleClose={setShowDetailsScreen}
                        assignmentData={assignMentProps}
                    />
                )}
                {openAlert ? (
                    <Alert variant="filled" severity="error" onClose={handleAlertClose} className={classes.errorAlert}>
                        {alertMesage}
                    </Alert>
                ) : null}

                {sim_listing && simsList && simsList.length === 0 ?
                    emptyListCheck ?
                        <h4 className={classes.noDataFound}>
                            Looks empty over here!
                        </h4> : (
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    height: "70vh",
                                }}
                            >
                                <CircularProgress color="primary"/>
                            </div>
                        ) : (
                        <Paper>
                            <TableContainer component={Paper} className={classes.root}>
                                <Table aria-label="sims table">
                                    <TableHead className={classes.thead}>
                                        <TableRow>
                                            <TableCell className={classes.heading}
                                                       sortDirection={sortBy === 'title' ? order : false}>
                                                <TableSortLabel
                                                    direction={sortBy === 'title' ? order : 'asc'}
                                                    active
                                                    IconComponent={ArrowDropDownIcon}
                                                    onClick={() => sortProperties('title')}
                                                >
                                                    CALL SIMULATOR
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell className={classes.heading}>
                                                <TableSortLabel
                                                    direction={sortBy === 'type' ? order : 'asc'}
                                                    active
                                                    IconComponent={ArrowDropDownIcon}
                                                    onClick={() => sortProperties('type')}
                                                >
                                                    TYPE
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell className={classes.heading}>
                                                <TableSortLabel
                                                    direction={sortBy === 'created_at' ? order : 'asc'}
                                                    active
                                                    IconComponent={ArrowDropDownIcon}
                                                    onClick={() => sortProperties('created_at')}
                                                >
                                                    CREATED
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell className={classes.heading}>
                                                <TableSortLabel
                                                    direction={sortBy === 'updated_at' ? order : 'asc'}
                                                    active
                                                    IconComponent={ArrowDropDownIcon}
                                                    onClick={() => sortProperties('updated_at')}
                                                >
                                                    UPDATED
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell className={classes.heading}>
                                                <TableSortLabel
                                                    direction={sortBy === 'admin_note' ? order : 'asc'}
                                                    active
                                                    IconComponent={ArrowDropDownIcon}
                                                    onClick={() => sortProperties('admin_note')}
                                                >
                                                    ADMIN NOTES
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell className={classes.heading} padding="none">
                                                <TableSortLabel
                                                    direction={sortBy === 'draft' ? order : 'asc'}
                                                    active
                                                    IconComponent={ArrowDropDownIcon}
                                                    onClick={() => sortProperties('draft')}
                                                >
                                                    DRAFT
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell className={classes.heading}>
                                                <TableSortLabel
                                                    direction={sortBy === 'first_name' ? order : 'asc'}
                                                    active
                                                    IconComponent={ArrowDropDownIcon}
                                                    onClick={() => sortProperties('first_name')}
                                                >
                                                    BUILT BY
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell className={classes.heading}>ACTIONS</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {simsList.length > 0
                                            ? simsList.map((row, index) => (
                                                <StyledTableRow
                                                    key={index}
                                                    className={classes.tableRow}
                                                >
                                                    <TableCell className={classes.bold}>
                                                        {row.screen_recording && row.screen_recording.length ?
                                                            <Tooltip
                                                                title="Has Screen Recording Enabled"
                                                                placement="top"
                                                            >
                                                                <div style={{
                                                                    marginRight: '1em',
                                                                    verticalAlign: 'middle',
                                                                    display: 'inline-block'
                                                                }}>
                                                                    <ScreenShareIcon fontSize="small"/>
                                                                </div>
                                                            </Tooltip> :
                                                            <></>
                                                        }

                                                        <Tooltip
                                                            title={"This SIM has " + row.totalLevels + " Levels and " + row.totalSteps + " scripts in total"}
                                                            placement="top"
                                                        >
                                                            <span>{row.title}</span>
                                                        </Tooltip>

                                                    </TableCell>
                                                    <TableCell className={classes.type}>
                                                        {products &&
                                                            products.map((product) => {
                                                                return row.product_id === product._id
                                                                    ? product.name
                                                                    : null;
                                                            })}
                                                    </TableCell>
                                                    <TableCell className={classes.created}>
                                                        {moment(row.created_at).format("MMM DD YYYY")}
                                                    </TableCell>
                                                    <TableCell className={classes.type}>
                                                        {moment(row.updated_at).startOf("minute").fromNow()}
                                                    </TableCell>
                                                    <TableCell
                                                        title={
                                                            row.admin_note?.length > 120
                                                                ? row.admin_note
                                                                : null
                                                        }
                                                        className={classes.admin_note}
                                                    >
                                                        {row?.admin_note?.length > 120
                                                            ? `${row.admin_note.slice(0, 120)}  ...`
                                                            : row.admin_note}
                                                    </TableCell>
                                                    <TableCell padding="none">
                                                        {row.draft ? "Yes" : "No"}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className={classes.built}>
                                                            <Avatar
                                                                src={
                                                                    row.profile_pic_url ? row.profile_pic_url : ProfilePic
                                                                }
                                                                alt="profile"
                                                                style={{marginRight: "10px"}}
                                                                className={classes.small}
                                                            />
                                                            <div>{`${row.first_name} ${row.last_name}`}</div>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell key={`actions_${row.id}`}>
                                                        <div className={classes.built}>
                                                            {trash ?
                                                                <Tooltip
                                                                    title="Restore"
                                                                    placement="top"
                                                                >
                                <span>
                                  <Button
                                      variant="contained"
                                      size="small"
                                      color="primary"
                                      className={classes.buttonSqr}
                                      disableElevation
                                      onClick={() => restoreSim(row)}
                                  >
                                      <RestoreFromTrashIcon
                                          fontSize="small"
                                      />
                                  </Button>
                                </span>
                                                                </Tooltip> :
                                                                <>
                                                                    <ThemeProvider theme={innerTheme}>
                                                                        <Button
                                                                            variant="contained"
                                                                            size="small"
                                                                            color="primary"
                                                                            className={classes.buttonSqr}
                                                                            disableElevation
                                                                        >
                                                                            <VisibilityIcon
                                                                                onClick={() => handleRowClick(row)}
                                                                                fontSize="small"
                                                                            />
                                                                        </Button>
                                                                    </ThemeProvider>
                                                                    {permissionCheck(
                                                                                SimsPermissions.sim_list.update.task,
                                                                                SimsPermissions.sim_list.update.type
                                                                    ) && (
                                                                        <Tooltip
                                                                            title="Edit this SIM"
                                                                        placement="bottom"
                                                                    >
                                  <span>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="primary"
                                        className={classes.buttonSqr}
                                        disableElevation
                                    >
                                      <EditIcon
                                          onClick={() => handleClick(row)}
                                          fontSize="small"
                                      />
                                    </Button>
                                  </span>
                                                                    </Tooltip>
                                                                    )}
                                                                    {permissionCheck(
                                                                                SimsPermissions.sim_list.delete.task,
                                                                                SimsPermissions.sim_list.delete.type
                                                                    ) && (
                                                                        <Tooltip
                                                                            title="Delete this SIM"
                                                                        placement="bottom"
                                                                    >
                                  <span>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        className={classes.buttonDelete}
                                        disableElevation
                                    >
                                      <DeleteIcon
                                          onClick={() => handleDeleteClick(row)}
                                          fontSize="small"
                                      />
                                    </Button>
                                  </span>
                                                                    </Tooltip>
                                                                    )}
                                                                </>
                                                            }
                                                        </div>
                                                    </TableCell>
                                                </StyledTableRow>
                                            ))
                                            : null}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <div>
                                <div className={classes.paginationLabel}>
                                    Showing
                                    {' '}
                                    {(page - 1) * 10 + 1}
                                    {' '}
                                    to
                                    {' '}
                                    {(page - 1) * 10
                                        + (simsList && simsList.length ? simsList.length : 0)}
                                    {' '}
                                    of
                                    {' '}
                                    {trash ? totalDeletedSims ? totalDeletedSims : 0 : totalSims ? totalSims : 0}
                                    {' '}
                                    entries
                                </div>
                                <Pagination
                                    className={classes.pagination}
                                    color="primary"
                                    count={
                                        trash && totalDeletedSims ?
                                            Math.ceil(totalDeletedSims / 10) :
                                            totalSims
                                                ? Math.ceil(totalSims / 10)
                                                : 0
                                    }
                                    shape="rounded"
                                    page={page}
                                    onChange={handlePageChange}
                                />
                            </div>
                        </Paper>
                    )}
                <Dialog
                    open={open}
                    onClose={handleClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle
                        id="alert-dialog-title">{trash ? emptyTrash ? 'Delete All' : 'Restore Sim' : 'Delete Sim'}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            {trash ? emptyTrash ?
                                    `Are you sure you want to permanently delete all the sims from deleted list` : `Do you want to restore the sim "${selectedRestoreSim && selectedRestoreSim.title}"?` :
                                `Do you want to delete the sim "${currentSim.title}"?`}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose} className={classes.cancelButton}>
                            Cancel
                        </Button>
                        {
                            trash ?
                                emptyTrash ?
                                    <Button
                                        onClick={deleteAllConfirm}
                                        className={classes.dialogDeleteBtn}
                                        autoFocus
                                    >
                                        Delete All
                                    </Button> :
                                    <Button
                                        onClick={restoreConfirm}
                                        className={classes.dialogDeleteBtn}
                                        autoFocus
                                    >
                                        Restore
                                    </Button> :
                                <Button
                                    onClick={() => deleteConfirm()}
                                    className={classes.dialogDeleteBtn}
                                    autoFocus
                                >
                                    Delete
                                </Button>
                        }
                    </DialogActions>
                </Dialog>
            </Paper>
            {!sim_listing && simsList.length === 0 ? (
                <div className={userDetails.role.toLowerCase() === 'agent' ? classes.hidePage : classes.noData}>
                    <img src={noSims} height={55} width={54} alt="noSims"/>
                    <h3 className={classes.noAssign}>There are no SIMs added yet!</h3>
                    <p className={classes.noAssignPara}>
                        Are you up for creating new SIM now?
                    </p>
                    <StyledLink to="/sims/createsim">
                        <Button
                            variant="outlined"
                            color="primary"
                            className={classes.addButton}
                            onClick={() => {
                                initializeNewSim();
                            }}
                        >
                            <span className={classes.plus}>+</span> ADD NEW SIM
                        </Button>
                    </StyledLink>
                </div>
            ) : null}
        </>
    );
};

/**
 * Defines the type-checking for the `Sims` component's props using PropTypes.
 *
 * This property ensures that the component's props adhere to the defined types and structure,
 * providing runtime validation of the component's expected prop types.
 */
Sims.propTypes = {
    totalSims: PropTypes.number,
    sims: PropTypes.array,
    fetchData: PropTypes.func,
    initializeNewSim: PropTypes.func,
    setEditSimStep: PropTypes.func,
    fetchProductList: PropTypes.func,
    deleteAssignmentDetails: PropTypes.func,
    saveSelectedSim: PropTypes.func,
    products: PropTypes.array,
    prop: PropTypes.object,
    selectedSim: PropTypes.object,
    reset: PropTypes.func,
    resetVideoStats: PropTypes.func,
    sim_listing: PropTypes.bool,
    userDetails: PropTypes.object,
    sfReset: PropTypes.func,
    saveCurrentSimProductType: PropTypes.func,
    characters: PropTypes.array,
    deletedSims: PropTypes.array,
    fetchDeletedSims: PropTypes.func,
    totalDeletedSims: PropTypes.number,
};

/**
 * Maps the state and props to a structured set of properties for use in a component.
 * This function connects the Redux store state and passed props to the component's props.
 *
 * @param {Object} state - The current state of the Redux store.
 * @param {Object} props - The props passed to the component.
 * @returns {Object} A mapping of the state and props to component props:
 * - `isLoading` {boolean}: Indicates whether a loading operation is in progress.
 * - `totalSims` {number}: The total number of sims.
 * - `sims` {Array}: The data associated with sims.
 * - `status` {string}: The status of the sim operation.
 * - `products` {Array}: A list of products associated with sims.
 * - `prop` {Object}: A derived property based on the `props` or local storage data.
 * - `selectedSim` {Object}: The currently selected sim.
 * - `characters` {Array}: An array of characters related to a new sim creation.
 * - `sim_listing` {Array}: A list of sims available for selection.
 * - `userDetails` {Object}: Information about the currently logged-in user.
 * - `deletedSims` {Array|null}: A list of deleted sims, if available.
 * - `totalDeletedSims` {number|null}: The total count of deleted sims, if available.
 */
const mapStateToProps = (state, props) => ({
    isLoading: state.sim.isLoading,
    totalSims: state.sim.total,
    sims: state.sim.data,
    status: state.sim.status,
    products: state.sim.products,
    prop: props && props.location.state ? props.location.state : localStorage.getItem('state') ? JSON.parse(localStorage.getItem('state')) : {},
    selectedSim: state.sim.selectedSim,
    characters: state.sim.newSim.sim_characters,
    sim_listing: state.sim.sim_listing,
    userDetails: state.login.user,
    deletedSims: state.sim.deletedSims && state.sim.deletedSims.sims,
    totalDeletedSims: state.sim.deletedSimsCount && state.sim.deletedSimsCount,
});

/**
 * An object that maps dispatchable action creators to props for a component.
 *
 * @property {Function} fetchData - Dispatches an action to fetch data.
 * @property {Function} initializeNewSim - Dispatches an action to initialize a new simulation.
 * @property {Function} setEditSimStep - Dispatches an action to set the current step for editing a simulation.
 * @property {Function} fetchProductList - Dispatches an action to fetch the list of products.
 * @property {Function} deleteAssignmentDetails - Dispatches an action to delete assignment details.
 * @property {Function} saveSelectedSim - Dispatches an action to save the selected simulation.
 * @property {Function} reset - Dispatches an action to reset the state or component.
 * @property {Function} resetVideoStats - Dispatches an action to reset video-related statistics.
 * @property {Function} sfReset - Dispatches an action to reset Salesforce-related data.
 * @property {Function} fetchDeletedSims - Dispatches an action to fetch deleted simulations.
 * @property {Function} saveCurrentSimProductType - Dispatches an action to save the current simulation's product type.
 */
const mapDispatchToProps = {
    fetchData,
    initializeNewSim,
    setEditSimStep,
    fetchProductList,
    deleteAssignmentDetails,
    saveSelectedSim,
    reset,
    resetVideoStats,
    sfReset,
    fetchDeletedSims,
    saveCurrentSimProductType
};

export default connect(mapStateToProps, mapDispatchToProps)(Sims);
